import React, { useMemo } from 'react';
import { buildDueDateFields } from './buildDueDateFields';
import { useDueDatePickerState } from './hooks/useDueDatePickerState';
import { useRelativeTimeTick } from './hooks/useRelativeTimeTick';
import { useTaskEstimatedDuration } from './hooks/useTaskEstimatedDuration';
import { DueDateFieldRow } from './components/DueDateFieldRow';

export { EstimatedDurationInputs } from './components/EstimatedDurationInputs';
export { useRelativeTimeTick } from './hooks/useRelativeTimeTick';

/**
 * DueDateDisplay — date fields as full-width rows (label left, value right).
 * Click opens a custom date picker with text input support.
 */
function DueDateDisplay({
  task,
  showDueDate,
  showEstimatedDuration,
  showCreatedAt,
  showStartedAt,
  showCompletedAt,
  showUpdatedAt,
  onDueDateChange,
  onEstimatedDurationMinutesChange,
  onCreatedAtChange,
  onStartedAtChange,
  onCompletedAtChange,
  onUpdatedAtChange,
  classPrefix,
  isTemplate = false,
  statuses = [],
  nestedSprints = [],
  allSprints = [],
}) {
  const relativeTimeTick = useRelativeTimeTick();
  const {
    minutes: estimatedMinutes,
    minMinutes: estimatedMinMinutes,
    maxMinutes: estimatedMaxMinutes,
    isNestedRollup,
  } = useTaskEstimatedDuration(task, {
    statuses,
    nestedSprints,
    allSprints,
  });

  const fields = useMemo(
    () =>
      buildDueDateFields({
        task,
        isTemplate,
        showDueDate,
        showEstimatedDuration,
      showCreatedAt,
      showStartedAt,
      showCompletedAt,
      showUpdatedAt,
      onDueDateChange,
      onEstimatedDurationMinutesChange,
      onCreatedAtChange,
      onStartedAtChange,
      onCompletedAtChange,
      onUpdatedAtChange,
      }),
    [
      task,
      isTemplate,
      showDueDate,
      showEstimatedDuration,
      showCreatedAt,
      showStartedAt,
      showCompletedAt,
      showUpdatedAt,
      onDueDateChange,
      onEstimatedDurationMinutesChange,
      onCreatedAtChange,
      onStartedAtChange,
      onCompletedAtChange,
      onUpdatedAtChange,
      relativeTimeTick,
    ]
  );

  const pickerState = useDueDatePickerState(task);

  const visibleFields = fields.filter((f) => f.show);

  if (visibleFields.length === 0) return null;

  return (
    <div className={`${classPrefix}__dates-section`}>
      {visibleFields.map((field) => (
        <DueDateFieldRow
          key={field.id}
          field={field}
          task={task}
          classPrefix={classPrefix}
          estimatedMinutes={field.inputType === 'duration' ? estimatedMinutes : undefined}
          estimatedMinMinutes={field.inputType === 'duration' ? estimatedMinMinutes : undefined}
          estimatedMaxMinutes={field.inputType === 'duration' ? estimatedMaxMinutes : undefined}
          isNestedDurationRollup={field.inputType === 'duration' ? isNestedRollup : false}
          openPicker={pickerState.openPicker}
          pickerPosition={pickerState.pickerPosition}
          tooltipField={pickerState.tooltipField}
          tooltipPosition={pickerState.tooltipPosition}
          valueRefs={pickerState.valueRefs}
          portalRef={pickerState.portalRef}
          setTooltipField={pickerState.setTooltipField}
          handleFieldClick={pickerState.handleFieldClick}
          handleFieldMouseLeave={pickerState.handleFieldMouseLeave}
          handleDateChange={pickerState.handleDateChange}
          handleClear={pickerState.handleClear}
          onDismissPicker={pickerState.closePicker}
        />
      ))}
    </div>
  );
}

export default DueDateDisplay;
